import React from 'react';
import { useIntl } from 'react-intl';
import { Switch } from '@/components/atoms/Switch';
import { SwitchGroupContainer } from './SwitchGroup.style';
import { Label } from '../Label';
import LabelWithTooltip from '../Form/LabelWithTooltip/LabelWithTooltip';
import { computeText } from '@/locales/utils';

interface SwitchOption<T> {
    labelId: string;
    name: T;
    value: boolean;
    disabled?: boolean;
    tooltip?: {
        text: string;
        title: string;
        example: string;
    };
}

interface SwitchGroupProps<T extends Record<string, boolean>> {
    value: T;
    onChange: (value: T) => void;
    options: SwitchOption<keyof T>[];
    labelId?: string;
    required?: boolean;
    /**
     * If true, ensures that only one switch in the group can be active at a time.
     * When a switch is toggled on, all other switches in the group are toggled off.
     */
    isUnique?: boolean;
}

export const SwitchGroup = <T extends Record<string, boolean>>({
    options,
    labelId,
    required,
    value,
    onChange,
    isUnique,
}: SwitchGroupProps<T>) => {
    const intl = useIntl();

    const onChangeHandler = (name: keyof T) => {
        const newValues = { ...value };
        if (isUnique) {
            Object.keys(newValues).forEach((key) => {
                newValues[key as keyof T] = (key === name ? !newValues[name] : false) as T[keyof T];
            });
        } else {
            newValues[name] = !value[name] as T[keyof T];
        }
        onChange(newValues);
    };

    return (
        <SwitchGroupContainer>
            {labelId && <Label labelId={labelId} inputId={labelId} required={required} />}
            {options.map((option, index) => {
                const customLabel = option.tooltip ? (
                    <LabelWithTooltip
                        text={computeText(intl, option.labelId)}
                        helperText={option.tooltip.text}
                        tooltipTitle={option.tooltip.title}
                        tooltipExample={option.tooltip.example}
                        id={`${String(option.name)}-tooltip`}
                    />
                ) : undefined;

                return (
                    <Switch
                        key={index}
                        labelId={!customLabel ? option.labelId : undefined}
                        customLabel={customLabel}
                        value={value[option.name] ?? false}
                        onChange={() => onChangeHandler(option.name)}
                        disabled={option?.disabled ?? false}
                    />
                );
            })}
        </SwitchGroupContainer>
    );
};
